import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, Thead } from "@/components/admin/AdminUI";

export default function AdminCrawlerResults() {
  const { id } = useParams();
  const qc = useQueryClient();

  const { data: brand } = useQuery({ queryKey: ["admin-brand", id], queryFn: async () => (await api.get(`/admin/brands/${id}`)).data });
  const { data: results = [] } = useQuery({
    queryKey: ["crawler-results", id],
    queryFn: async () => (await api.get(`/admin/brands/${id}/crawler-results`)).data,
  });

  async function convert(rid) {
    try {
      await api.post(`/admin/clothing-items/from-crawler-result/${rid}`);
      toast.success("Converted to clothing item — saved as Pending");
      // Stay on this page so the admin can keep converting / deleting other
      // results in the queue. Refresh the list so the just-converted row
      // disappears (the backend deletes the crawler_result on conversion).
      qc.invalidateQueries({ queryKey: ["crawler-results", id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    }
  }

  async function deleteResult(rid) {
    if (!window.confirm("Delete this crawler result? If the URL still exists on the brand site it will reappear on the next crawl.")) return;
    try {
      await api.delete(`/admin/crawler-results/${rid}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["crawler-results", id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div data-testid="admin-crawler-results">
      <PageHeader
        title={`Crawler results — ${brand?.name || "..."}`}
        subtitle="Review and convert detected products. Crawler will never auto-publish."
        actions={<Link to="/admin/brands" className="text-sm underline" data-testid="back-to-brands-list">← Back to brands list</Link>}
      />

      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <Thead columns={["Image", "Product", "Detected type", "Materials text", "Price", "Actions"]} />
          <tbody>
            {results.map((r) => (
              <tr key={r.id} className="border-b border-[#E9ECEF]">
                <td className="px-3 py-3">{r.product_image_url ? <img src={r.product_image_url} alt="" className="w-12 h-12 object-cover rounded" /> : "—"}</td>
                <td className="px-3 py-3 max-w-[240px]">
                  <div className="font-medium truncate">{r.product_name || "—"}</div>
                  <a href={r.product_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline truncate block">{r.product_url}</a>
                </td>
                <td className="px-3 py-3 text-xs">{r.detected_clothing_type || "—"}</td>
                <td className="px-3 py-3 text-xs max-w-[180px] truncate">{r.detected_material_text || "—"}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{r.price ? `${r.price} ${r.currency || ""}` : "—"}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">
                  <button onClick={() => convert(r.id)} className="text-blue-600 underline" data-testid={`convert-${r.id}`}>Convert</button>
                  <span className="text-[#CED4DA] mx-1">·</span>
                  <button onClick={() => deleteResult(r.id)} className="text-clay underline" data-testid={`delete-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {results.length === 0 && <tr><td colSpan={6} className="px-3 py-12 text-center text-[#6C757D]">No results yet. Run the crawler from the brand page.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
